/**
 * Policy Framework Manager
 * 
 * Component for managing policy frameworks
 * Part of Policy Framework & EU Diseases Implementation
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePolicyFrameworks, usePolicyFrameworkMutations } from '../../hooks/usePolicyFrameworks';
import { useNotifications } from '../NotificationSystem';
import { departmentService } from '../../services/firebaseService';
import Modal from '../ui/Modal';
import './PolicyFrameworkManager.css';

const PolicyFrameworkManager = ({ organizationId, userId, userName }) => {
  const { t } = useTranslation('ipc');
  const { frameworks, loading, error, refresh } = usePolicyFrameworks(organizationId);
  const { createFramework, updateFramework, deleteFramework, loading: mutating } = usePolicyFrameworkMutations();
  const { notify } = useNotifications();

  const [showForm, setShowForm] = useState(false);
  const [editingFramework, setEditingFramework] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [frameworkToDelete, setFrameworkToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    code: '',
    scope: 'hospital-wide',
    departmentId: '',
    departmentName: '',
    authority: '',
    status: 'active',
    reviewFrequency: 'annual',
    complianceMandatory: false
  });
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  useEffect(() => {
    if (organizationId && formData.scope === 'department-specific') {
      loadDepartments();
    }
  }, [organizationId, formData.scope]);

  const loadDepartments = async () => {
    if (!organizationId) return;
    
    setLoadingDepartments(true);
    try {
      // Get all departments for this organization
      // Note: Departments are linked via floors/buildings, so we need to query through the hierarchy
      // For now, we'll get all departments and filter by organizationId if available
      const allDepartments = await departmentService.getAll();
      const orgDepartments = allDepartments.filter(dept => 
        dept.organizationId === organizationId || !dept.organizationId
      );
      setDepartments(orgDepartments);
    } catch (err) {
      notify.error('Failed to load departments: ' + err.message);
    } finally {
      setLoadingDepartments(false);
    }
  };

  const handleCreate = () => {
    setEditingFramework(null);
    setFormData({
      name: '',
      description: '',
      code: '',
      scope: 'hospital-wide',
      departmentId: '',
      departmentName: '',
      authority: '',
      status: 'active',
      reviewFrequency: 'annual',
      complianceMandatory: false
    });
    setShowForm(true);
  };

  const handleEdit = (framework) => {
    setEditingFramework(framework);
    setFormData({
      name: framework.name || '',
      description: framework.description || '',
      code: framework.code || '',
      scope: framework.scope || 'hospital-wide',
      departmentId: framework.departmentId || '',
      departmentName: framework.departmentName || '',
      authority: framework.authority || '',
      status: framework.status || 'active',
      reviewFrequency: framework.reviewFrequency || 'annual',
      complianceMandatory: framework.complianceMandatory || false
    });
    setShowForm(true);
    if (framework.scope === 'department-specific') {
      loadDepartments();
    }
  };

  const handleDelete = (frameworkId) => {
    setFrameworkToDelete(frameworkId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteFramework(frameworkToDelete, userId, userName);
      notify.success(t('frameworkArchived'));
      refresh();
      setShowDeleteConfirm(false);
      setFrameworkToDelete(null);
    } catch (err) {
      notify.error(err.message || 'Failed to archive policy framework');
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.name) {
        notify.error(t('frameworkNameRequired'));
        return;
      }

      if (formData.scope === 'department-specific' && !formData.departmentId) {
        notify.error(t('departmentRequired'));
        return;
      }

      // Prepare data - only include departmentId if scope is department-specific
      const saveData = {
        ...formData
      };
      
      if (formData.scope === 'hospital-wide') {
        // Clear department fields for hospital-wide frameworks
        saveData.departmentId = null;
        saveData.departmentName = null;
      }

      if (editingFramework) {
        await updateFramework(editingFramework.id, saveData, userId, userName);
        notify.success(t('frameworkUpdated'));
      } else {
        await createFramework({
          ...saveData,
          organizationId
        }, userId, userName);
        notify.success('Policy framework created successfully');
      }

      setShowForm(false);
      setEditingFramework(null);
      refresh();
    } catch (err) {
      notify.error(err.message || t('failedToSaveFramework'));
    }
  };

  if (loading) {
    return (
      <div className="policy-framework-manager">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('loadingFrameworks')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="policy-framework-manager">
      <div className="manager-header">
        <h2>{t('policyFrameworks')}</h2>
        <button 
          className="btn btn-primary"
          onClick={handleCreate}
          disabled={!organizationId}
        >
          {t('addFramework')}
        </button>
        {!organizationId && (
          <div className="error-message" style={{ marginTop: '16px' }}>
            ⚠️ {t('noOrganizationPolicy')}
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      <div className="frameworks-list">
        {frameworks.length === 0 ? (
          <div className="empty-state">
            <p>{t('noFrameworksFound')}</p>
          </div>
        ) : (
          <table className="frameworks-table">
            <thead>
              <tr>
                <th>{t('code')}</th>
                <th>{t('name')}</th>
                <th>{t('scope')}</th>
                <th>{t('status')}</th>
                <th>{t('reviewFrequency')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {frameworks.map(framework => (
                <tr key={framework.id}>
                  <td>{framework.code || 'N/A'}</td>
                  <td className="framework-name">{framework.name}</td>
                  <td>{framework.scope || 'N/A'}</td>
                  <td>
                    <span className={`status-badge status-${framework.status || 'active'}`}>
                      {framework.status || 'active'}
                    </span>
                  </td>
                  <td>{framework.reviewFrequency || 'N/A'}</td>
                  <td className="actions">
                    <button
                      className="btn btn-sm btn-edit"
                      onClick={() => handleEdit(framework)}
                    >
                      {t('edit')}
                    </button>
                    <button
                      className="btn btn-sm btn-delete"
                      onClick={() => handleDelete(framework.id)}
                    >
                      {t('archive')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingFramework(null);
        }}
        title={editingFramework ? t('editPolicyFramework') : t('newPolicyFramework')}
        size="large"
      >
        <div className="framework-form">
          <div className="form-group">
            <label>
              {t('frameworkName')} <span className="required">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t('placeholderFrameworkName')}
            />
          </div>

          <div className="form-group">
            <label>{t('frameworkCode')}</label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              placeholder={t('placeholderFrameworkCode')}
            />
          </div>

          <div className="form-group">
            <label>{t('description')}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder={t('placeholderDescription')}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('scope')}</label>
              <select
                value={formData.scope}
                onChange={(e) => {
                  const newScope = e.target.value;
                  setFormData({ 
                    ...formData, 
                    scope: newScope,
                    departmentId: newScope === 'hospital-wide' ? '' : formData.departmentId,
                    departmentName: newScope === 'hospital-wide' ? '' : formData.departmentName
                  });
                  if (newScope === 'department-specific' && organizationId) {
                    loadDepartments();
                  }
                }}
              >
                <option value="hospital-wide">{t('hospitalWide')}</option>
                <option value="department-specific">{t('departmentSpecific')}</option>
              </select>
            </div>

            {formData.scope === 'department-specific' && (
              <div className="form-group">
                <label>
                  {t('department')} <span className="required">*</span>
                </label>
                {loadingDepartments ? (
                  <div className="loading-state">{t('loadingDepartments')}</div>
                ) : departments.length === 0 ? (
                  <div className="warning-message">
                    ⚠️ {t('noDepartmentsFound')}
                  </div>
                ) : (
                  <select
                    value={formData.departmentId}
                    onChange={(e) => {
                      const selectedDept = departments.find(d => d.id === e.target.value);
                      setFormData({ 
                        ...formData, 
                        departmentId: e.target.value,
                        departmentName: selectedDept ? selectedDept.name : ''
                      });
                    }}
                    required
                    style={{ width: '100%', padding: '8px' }}
                  >
                    <option value="">{t('selectDepartment')}</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name || dept.id}
                      </option>
                    ))}
                  </select>
                )}
                {formData.scope === 'department-specific' && !formData.departmentId && (
                  <div className="error-message" style={{ marginTop: '8px', fontSize: '12px' }}>
                    ⚠️ {t('departmentRequired')}
                  </div>
                )}
              </div>
            )}

            <div className="form-group">
              <label>{t('reviewFrequency')}</label>
              <select
                value={formData.reviewFrequency}
                onChange={(e) => setFormData({ ...formData, reviewFrequency: e.target.value })}
              >
                <option value="annual">{t('annual')}</option>
                <option value="biennial">{t('biennial')}</option>
                <option value="triennial">{t('triennial')}</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>{t('authority')}</label>
            <input
              type="text"
              value={formData.authority}
              onChange={(e) => setFormData({ ...formData, authority: e.target.value })}
              placeholder={t('placeholderAuthority')}
            />
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.complianceMandatory}
                onChange={(e) => setFormData({ ...formData, complianceMandatory: e.target.checked })}
              />
              {t('complianceMandatory')}
            </label>
          </div>

          <div className="form-actions">
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowForm(false);
                setEditingFramework(null);
              }}
            >
              {t('cancel')}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={mutating || !formData.name}
            >
              {editingFramework ? t('saveChanges') : t('createFramework')}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setFrameworkToDelete(null);
        }}
        title={t('archivePolicyFramework')}
        size="small"
      >
        <div className="delete-confirm">
          <p>{t('confirmArchiveFramework')}</p>
          <div className="form-actions">
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowDeleteConfirm(false);
                setFrameworkToDelete(null);
              }}
            >
              {t('cancel')}
            </button>
            <button
              className="btn btn-danger"
              onClick={confirmDelete}
            >
              {t('archive')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PolicyFrameworkManager;
